import { useEffect, useMemo, useState } from "react";
import Modal from "../common/Modal";
import { useAuth } from "../../context/AuthContext";
import {
  createScheduledReport,
  updateScheduledReport,
} from "../../firebase/scheduledReports";
import { detectBrowserTimeZone, computeScheduledAt } from "../../utils/timezoneScheduling";
import { MAX_SCHEDULE_RECIPIENTS } from "../../features/subscription/planConfig";
import { trackEvent } from "../../utils/analyticsEvents";
import { sendTestScheduledReport } from "../../firebase/functionsClient";
import { getUserRecipientLists } from "../../firebase/recipientLists";
import EmailChipInput from "./EmailChipInput";

// InsightPulse schedules a report to be sent ONCE, at a specific date and
// time — not on a recurring weekly/monthly cadence. See
// IMPLEMENTATION_ONE_TIME_SCHEDULING.md for the full rationale.

// A short curated list plus whatever the browser actually detects, so the
// select is never missing the visitor's own zone even if it's not in the
// curated list below.
const COMMON_TIME_ZONES = [
  "UTC",
  "America/Los_Angeles",
  "America/Denver",
  "America/Chicago",
  "America/New_York",
  "America/Sao_Paulo",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Africa/Lagos",
  "Africa/Cairo",
  "Asia/Dubai",
  "Asia/Karachi",
  "Asia/Kolkata",
  "Asia/Dhaka",
  "Asia/Bangkok",
  "Asia/Singapore",
  "Asia/Shanghai",
  "Asia/Tokyo",
  "Australia/Sydney",
  "Pacific/Auckland",
];

function formatScheduledPreview(date) {
  if (!date) return null;
  return date.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  });
}

// Tomorrow, in YYYY-MM-DD, as a sensible default so the date picker never
// opens on a value that's already in the past.
function defaultDate() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
}

// A schedule's existing `scheduledAt` (new shape) or `nextRunAt` (legacy
// recurring shape, kept readable so old documents still populate the form
// sensibly if ever re-opened) split back into separate date/time fields
// for the two native inputs.
function splitScheduledAt(existing) {
  const ts = existing?.scheduledAt || existing?.nextRunAt;
  if (!ts?.toDate) return { date: defaultDate(), time: "09:00" };

  const d = ts.toDate();
  const pad = (n) => String(n).padStart(2, "0");
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
}

function defaultFormState(session, existing) {
  const zone = existing?.timezone || detectBrowserTimeZone();
  const { date, time } = splitScheduledAt(existing);

  if (existing) {
    return {
      reportName: existing.reportName || "",
      date,
      time,
      timezone: zone,
      recipients: existing.recipients || [],
      recipientListIds: existing.recipientListIds || [],
      includeMetrics: existing.includeMetrics ?? true,
      includeAiSummary: existing.includeAiSummary ?? true,
      includeDashboardLink: existing.includeDashboardLink ?? true,
    };
  }

  return {
    reportName: session?.name ? `${session.name} — Report` : "",
    date,
    time,
    timezone: zone,
    recipients: [],
    recipientListIds: [],
    includeMetrics: true,
    includeAiSummary: true,
    includeDashboardLink: true,
  };
}

// A schedule can only be edited while it hasn't fired yet. Legacy
// documents (created before one-time scheduling) have no `status` field
// at all — treat those as editable too, exactly like "scheduled", since
// they haven't been migrated/sent yet either.
function isEditable(existing) {
  if (!existing?.id) return true; // creating new
  return !existing.status || existing.status === "scheduled";
}

export default function ScheduleReportModal({ open, onClose, session, existing, onSaved }) {
  const { user } = useAuth();

  const [form, setForm] = useState(() => defaultFormState(session, existing));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [success, setSuccess] = useState(false);

  const [recipientLists, setRecipientLists] = useState([]);
  const [listsLoading, setListsLoading] = useState(false);

  const [testStatus, setTestStatus] = useState(null); // null | "sending" | "sent" | "error"

  const editable = isEditable(existing);

  useEffect(() => {
    if (open) {
      setForm(defaultFormState(session, existing));
      setErrors({});
      setSaveError(null);
      setSuccess(false);
      setTestStatus(null);
      trackEvent("scheduled_report_opened");

      if (user) {
        setListsLoading(true);
        getUserRecipientLists(user.uid)
          .then(setRecipientLists)
          .catch((err) => console.error("Failed to load recipient lists:", err))
          .finally(() => setListsLoading(false));
      }
    }
    // Only reset when the modal is (re)opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const timeZoneOptions = useMemo(() => {
    const set = new Set(COMMON_TIME_ZONES);
    set.add(form.timezone);
    return Array.from(set).sort();
  }, [form.timezone]);

  const scheduledPreview = useMemo(() => {
    try {
      return formatScheduledPreview(computeScheduledAt(form));
    } catch {
      return null;
    }
  }, [form]);

  const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  // -----------------------------
  // RECIPIENT LISTS
  // -----------------------------
  const toggleRecipientList = (listId) => {
    setForm((prev) => {
      const selected = prev.recipientListIds.includes(listId);
      return {
        ...prev,
        recipientListIds: selected
          ? prev.recipientListIds.filter((id) => id !== listId)
          : [...prev.recipientListIds, listId],
      };
    });
    setErrors((prev) => ({ ...prev, recipients: null }));
  };

  // -----------------------------
  // VALIDATION
  // -----------------------------
  const validate = () => {
    const next = {};

    if (form.recipients.length === 0 && form.recipientListIds.length === 0) {
      next.recipients = "Add at least one recipient, or select a recipient list.";
    }

    if (!form.date) {
      next.date = "Choose a date.";
    }

    if (!form.time) {
      next.time = "Choose a time.";
    }

    const scheduledAt = computeScheduledAt(form);
    if (form.date && form.time && scheduledAt && scheduledAt.getTime() <= Date.now()) {
      next.date = "Choose a date and time in the future.";
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  // -----------------------------
  // SAVE
  // -----------------------------
  const handleSave = async (e) => {
    e.preventDefault();

    if (saving || !editable) return; // prevent duplicate accidental submissions
    if (!validate()) return;

    // A schedule must always point at a real, already-saved session — this
    // is not a value the UI ever invents. Creating one without a valid
    // session id is refused outright rather than silently written.
    if (!existing?.id && !session?.firebaseId) {
      setSaveError("Select a saved dataset/session before scheduling a report.");
      return;
    }

    setSaving(true);
    setSaveError(null);

    const payload = {
      ...form,
      reportName: form.reportName.trim() || defaultFormState(session, existing).reportName,
    };

    try {
      if (existing?.id) {
        await updateScheduledReport(existing.id, payload);
        trackEvent("scheduled_report_updated");
      } else {
        await createScheduledReport(user.uid, session.firebaseId, payload);
        trackEvent("scheduled_report_created");
      }

      setSuccess(true);
      onSaved?.();
    } catch (err) {
      console.error("Failed to save scheduled report:", err);
      setSaveError(err?.message || "We couldn't save this schedule. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // -----------------------------
  // SEND TEST REPORT
  // -----------------------------
  const handleSendTest = async () => {
    if (!existing?.id || testStatus === "sending") return;

    setTestStatus("sending");

    try {
      const token = await user.getIdToken();
      await sendTestScheduledReport(existing.id, token);
      setTestStatus("sent");
      trackEvent("scheduled_report_test_sent");
    } catch (err) {
      console.error("Test report failed:", err);
      setTestStatus("error");
    } finally {
      setTimeout(() => setTestStatus((s) => (s === "sending" ? null : s)), 4000);
    }
  };

  const inputClass =
    "w-full bg-gray-950 border border-gray-700 focus:border-blue-500 rounded-lg px-3 py-2 text-sm text-white outline-none disabled:opacity-60";
  const labelClass = "block text-xs font-medium text-gray-400 mb-1.5";

  if (success) {
    return (
      <Modal
        open={open}
        onClose={onClose}
        title={existing?.id ? "Schedule updated" : "Report scheduled"}
        footer={
          <button
            onClick={onClose}
            className="px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 transition text-sm font-semibold"
          >
            Done
          </button>
        }
      >
        <div className="flex items-start gap-3">
          <span className="text-emerald-400 text-lg mt-0.5" aria-hidden="true">✓</span>
          <div>
            <p>
              <span className="text-white font-medium">{form.reportName}</span> will be sent once, to{" "}
              {form.recipients.length} recipient{form.recipients.length === 1 ? "" : "s"}
              {form.recipientListIds.length > 0 && (
                <> plus {form.recipientListIds.length} list{form.recipientListIds.length === 1 ? "" : "s"}</>
              )}.
            </p>
            {scheduledPreview && (
              <p className="mt-2 text-gray-400 text-sm">
                Scheduled for: <span className="text-gray-200">{scheduledPreview}</span>
              </p>
            )}
            <p className="mt-2 text-xs text-gray-500">
              This report is sent exactly once and will not repeat.
            </p>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal open={open} onClose={onClose} dismissible={!saving} title={existing?.id ? "Edit Scheduled Report" : "Schedule Report"} size="lg">

      {!editable && (
        <div className="mb-4 bg-gray-800/60 border border-gray-700 rounded-lg px-3 py-2.5 text-xs text-gray-400">
          This report has already {existing?.status === "failed" ? "failed to send" : existing?.status === "sent" ? "been sent" : "been processed"} and can no longer be edited.
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-5" noValidate>

        {/* REPORT NAME */}
        <div>
          <label htmlFor="report-name" className={labelClass}>Report name</label>
          <input
            id="report-name"
            type="text"
            value={form.reportName}
            onChange={(e) => setField("reportName", e.target.value)}
            placeholder={defaultFormState(session, existing).reportName}
            disabled={saving || !editable}
            className={inputClass}
          />
        </div>

        {/* DATE + TIME */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="schedule-date" className={labelClass}>Date</label>
            <input
              id="schedule-date"
              type="date"
              value={form.date}
              min={new Date().toISOString().slice(0, 10)}
              onChange={(e) => setField("date", e.target.value)}
              disabled={saving || !editable}
              className={inputClass}
            />
            {errors.date && <p className="mt-1 text-xs text-red-400">{errors.date}</p>}
          </div>

          <div>
            <label htmlFor="schedule-time" className={labelClass}>Time</label>
            <input
              id="schedule-time"
              type="time"
              value={form.time}
              onChange={(e) => setField("time", e.target.value)}
              disabled={saving || !editable}
              className={inputClass}
            />
            {errors.time && <p className="mt-1 text-xs text-red-400">{errors.time}</p>}
          </div>
        </div>

        {/* TIME ZONE */}
        <div>
          <label htmlFor="schedule-timezone" className={labelClass}>Time zone</label>
          <select
            id="schedule-timezone"
            value={form.timezone}
            onChange={(e) => setField("timezone", e.target.value)}
            disabled={saving || !editable}
            className={inputClass}
          >
            {timeZoneOptions.map((tz) => (
              <option key={tz} value={tz}>{tz}</option>
            ))}
          </select>
        </div>

        {scheduledPreview && (
          <p className="text-xs text-gray-400 -mt-2">
            Will be sent once, on: <span className="text-gray-200 font-medium">{scheduledPreview}</span>
          </p>
        )}

        {/* RECIPIENT LISTS */}
        <div>
          <span className={labelClass}>Recipient lists</span>

          {listsLoading && (
            <p className="text-xs text-gray-500">Loading your lists...</p>
          )}

          {!listsLoading && recipientLists.length === 0 && (
            <p className="text-xs text-gray-500">
              You don't have any recipient lists yet — add one-off emails below, or create a reusable list
              from the &ldquo;Recipient Lists&rdquo; tab.
            </p>
          )}

          {!listsLoading && recipientLists.length > 0 && (
            <div className="space-y-1.5">
              {recipientLists.map((list) => {
                const checked = form.recipientListIds.includes(list.id);
                return (
                  <label
                    key={list.id}
                    className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg border text-sm transition ${
                      editable ? "cursor-pointer" : "cursor-not-allowed opacity-60"
                    } ${
                      checked ? "bg-blue-950/30 border-blue-800/60" : "bg-gray-950 border-gray-700 hover:border-gray-600"
                    }`}
                  >
                    <span className="flex items-center gap-2.5 min-w-0">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleRecipientList(list.id)}
                        disabled={saving || !editable}
                        className="w-4 h-4 rounded accent-blue-600 shrink-0"
                      />
                      <span className="truncate text-gray-200">{list.name}</span>
                    </span>
                    <span className="text-xs text-gray-500 shrink-0">
                      {list.recipients?.length || 0} recipient{(list.recipients?.length || 0) === 1 ? "" : "s"}
                    </span>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {/* AD-HOC RECIPIENTS */}
        <div>
          <label htmlFor="recipient-input" className={labelClass}>
            One-off recipients ({form.recipients.length}/{MAX_SCHEDULE_RECIPIENTS})
          </label>
          <EmailChipInput
            id="recipient-input"
            emails={form.recipients}
            onChange={(emails) => { setField("recipients", emails); setErrors((prev) => ({ ...prev, recipients: null })); }}
            disabled={saving || !editable}
            max={MAX_SCHEDULE_RECIPIENTS}
          />
          {errors.recipients && <p className="mt-1.5 text-xs text-red-400">{errors.recipients}</p>}
        </div>

        {/* INCLUDED CONTENT */}
        <fieldset disabled={saving || !editable}>
          <legend className={labelClass}>Included content</legend>
          <div className="space-y-2">
            <label className="flex items-center gap-2.5 text-sm text-gray-300">
              <input
                type="checkbox"
                checked={form.includeMetrics}
                onChange={(e) => setField("includeMetrics", e.target.checked)}
                className="w-4 h-4 rounded accent-blue-600"
              />
              Key metrics
            </label>
            <label className="flex items-center gap-2.5 text-sm text-gray-300">
              <input
                type="checkbox"
                checked={form.includeAiSummary}
                onChange={(e) => setField("includeAiSummary", e.target.checked)}
                className="w-4 h-4 rounded accent-blue-600"
              />
              AI-generated summary
            </label>
            <label className="flex items-center gap-2.5 text-sm text-gray-300">
              <input
                type="checkbox"
                checked={form.includeDashboardLink}
                onChange={(e) => setField("includeDashboardLink", e.target.checked)}
                className="w-4 h-4 rounded accent-blue-600"
              />
              Dashboard link
            </label>
          </div>
        </fieldset>

        <p className="text-[11px] text-gray-500 leading-relaxed border-t border-gray-800 pt-3">
          Schedule this report to be sent once. It's generated from the latest saved data in this session
          at the moment it sends, and includes a PDF attachment — it will not repeat or refresh automatically.
        </p>

        {saveError && <p className="text-sm text-red-400">{saveError}</p>}

        <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-2.5 pt-1">
          <div>
            {existing?.id && (
              <button
                type="button"
                onClick={handleSendTest}
                disabled={testStatus === "sending"}
                className="text-xs px-3 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 border border-gray-700 transition font-medium disabled:opacity-50"
              >
                {testStatus === "sending" ? "Sending test..." :
                 testStatus === "sent" ? "✓ Test email sent" :
                 testStatus === "error" ? "Failed — try again" :
                 "Send Test Report"}
              </button>
            )}
          </div>

          <div className="flex gap-2.5">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2.5 rounded-xl bg-gray-800 hover:bg-gray-700 border border-gray-700 transition text-sm font-medium disabled:opacity-50"
            >
              {editable ? "Cancel" : "Close"}
            </button>
            {editable && (
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 transition text-sm font-semibold disabled:opacity-60 flex items-center gap-2"
              >
                {saving && <span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />}
                {saving ? "Saving..." : existing?.id ? "Save Changes" : "Schedule Report"}
              </button>
            )}
          </div>
        </div>

      </form>
    </Modal>
  );
}
